import {Component, OnDestroy, OnInit} from '@angular/core';
import {TabOrderStatus} from '../../../../core/models/order.model';
import {Customer} from '../../../../core/models/customer.model';
import {CartStore} from '../../../../core/stores/cart.store';
import {RestaurantStore} from '../../../../core/stores/restaurant.store';
import {PaymentStore} from '../../../../core/stores/payment.store';
import {Router} from '@angular/router';
import {OrderApiService} from '../../../../core/services/order.api.service';
import {RouteService} from '../../../../core/services/route.service';
import {STORAGE_TAB_DATA} from '../../../../core/constants';
import {StorageService} from '../../../../core/services/storage.service';
import {filter} from 'rxjs/operators';
import {GroupOrderTab, OrderTab, TabRound} from '../../../../core/models/payment-item.model';
import { Tip } from '../../../../core/enums/tip.enum';

@Component({
  selector: 'app-close-tab',
  templateUrl: './close-tab.component.html',
  styleUrls: ['./close-tab.component.scss']
})
export class CloseTabComponent implements OnInit {

  public isScrollStartPosition = true;
  public tabOrdersStatus: TabOrderStatus[] = [];
  public customer: Customer;
  public locationId: string;
  public isShowModal: boolean;
  public isLoading = false;
  public forceTouched = false;
  public tippingInvalid = false;
  public showErrorMessage = false;
  public currencySign = '$';

  public orderCounter = 0;
  public totalPrice = 0;
  public totalTaxesPrice = 0;
  public totalDiscountsPrice = 0;
  public total = 0;
  public kyooFee = 0;
  public tip = Tip;

  private readonly defaultTippingItems = [0, 1, 3, 5];

  public get tipAmounts() {
    return this.defaultTippingItems;
  }

  constructor(public cartStore: CartStore,
              public restaurantStore: RestaurantStore,
              public paymentStore: PaymentStore,
              private router: Router,
              private orderApiService: OrderApiService,
              private storageService: StorageService,
              private routeService: RouteService) {
  }

  ngOnInit() {
    this.customer = this.paymentStore.customer;
    this.locationId = this.routeService.getLocationId();
    if (this.cartStore.tab) {
      this.orderApiService.getTabById(this.cartStore.tab.tab_id).subscribe((tab: OrderTab) => {
        this.cartStore.tab = tab;
        this.totalDiscountsPrice = tab.total_discount_amount / 100;
        this.totalTaxesPrice = tab.total_tax_amount / 100;
        this.totalPrice = tab.subtotal_amount / 100;
        this.fillTabOrderStatus(tab.square_orders);
        this.total = this.totalPrice + this.totalTaxesPrice - this.totalDiscountsPrice;
        this.cartStore.setTip(this.cartStore.autoGratuityPercent * this.totalPrice / 100);
        this.currencySign = this.restaurantStore.restaurantCountryInfo.currencySign;

      });
    } else if (this.cartStore.groupTab) {
      this.orderApiService.getGroupTabById(this.cartStore.groupTab.group_order_id).subscribe( (tab: GroupOrderTab) => {
        this.cartStore.groupTab = tab;
        this.fillGroupTabOrderStatus(this.cartStore.groupTab.rounds);
        this.totalDiscountsPrice = tab.total_discount_amount / 100;
        this.totalTaxesPrice = tab.total_tax_amount / 100;
        this.totalPrice = tab.subtotal_amount / 100;
        this.total = this.totalPrice + this.totalTaxesPrice - this.totalDiscountsPrice;
        this.cartStore.setTip(this.cartStore.autoGratuityPercent * this.totalPrice / 100);
        this.currencySign = this.restaurantStore.restaurantCountryInfo.currencySign;
      });
    }
  }

  public updateHeaderLogo(e): void {
    this.isScrollStartPosition = e.detail.currentY === 0;
  }

  public getFormattedPrice(price: string | number, quantity = 1): string {
    return (+price * quantity).toFixed(2);
  }

  public closeModal(): void {
    this.isShowModal = false;
    this.isLoading = true;
    this.backToLocation();
  }

  public backToLocation(): void {
    this.restaurantStore.backToStorePage();
    this.cartStore.tab = null;
    this.cartStore.groupTab = null;
    this.cartStore.kyooFee = null;
    this.cartStore.additionalKyooFee = null;
  }

  public checkTabAndClose(): void {
    if (this.cartStore.tab) {
      this.orderApiService.getTabById(this.cartStore.tab.tab_id)
        .pipe(filter(Boolean))
        .subscribe((tab: OrderTab) => {
          if (tab.is_closed) {
            this.isShowModal = true;
          } else {
            this.closeTab();
          }
        }, (error) => {
          this.showErrorMessage = true;
          this.isLoading = false;
        });
    } else if (this.cartStore.groupTab) {
      this.orderApiService.getGroupTabById(this.cartStore.groupTab.group_order_id)
        .pipe(filter(Boolean))
        .subscribe((tab: GroupOrderTab) => {
          if (tab.is_closed) {
            this.isShowModal = true;
          } else {
            this.closeGroupTab();
          }
        }, (error) => {
          this.showErrorMessage = true;
          this.isLoading = false;
        });
    }
  }

  private closeTab(): void {
    const tab = this.cartStore.tab;
    const tipInCents = Math.round(this.cartStore.getTip(this.tip.restaurant) * 100);
    this.isLoading = true;
    this.showErrorMessage = false;
    this.orderApiService.closeTab(tab.location_id, tab.tab_id, tipInCents)
      .subscribe(() => {
        this.isLoading = false;
        this.storageService.remove(STORAGE_TAB_DATA);
        this.cartStore.tabClosedSuccessful = true;
        this.backToLocation();
      }, (error) => {
        this.isLoading = false;
        this.showErrorMessage = true;
      });
  }

  private closeGroupTab(): void {
    const tab = this.cartStore.groupTab;
    const tipInCents = Math.round(this.cartStore.getTip(this.tip.restaurant) * 100);
    this.isLoading = true;
    this.showErrorMessage = false;
    this.orderApiService.closeGroupTab(tab.group_id, tab.group_order_id, tipInCents)
      .subscribe(() => {
        this.isLoading = false;
        this.storageService.remove(STORAGE_TAB_DATA);
        this.cartStore.tabClosedSuccessful = true;
        this.backToLocation();
      }, (error) => {
        this.isLoading = false;
        this.showErrorMessage = true;
      });
  }

  private fillTabOrderStatus(orders: any[]): void {
    for (const order of orders) {
      this.orderCounter++;
      this.tabOrdersStatus.push(new TabOrderStatus(order.id, this.orderCounter.toString(), order.customer_id, order.line_items));
    }
  }

  private fillGroupTabOrderStatus(rounds: TabRound[]): void {
    for (const round of rounds) {
      this.orderCounter++;
      let roundOrderLineItems: any[][] = [];
      for (const order of round.round_orders) {
        roundOrderLineItems = [...roundOrderLineItems, order.line_items];
      }
      this.tabOrdersStatus.push(new TabOrderStatus(round.round_number.toString(), this.orderCounter.toString(), this.cartStore.groupTab.customer_id, roundOrderLineItems.flat()));
    }
  }
}
